import { ThemeEvents, DiscountUpdateEvent } from "./events.js";

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.appliedDiscounts = new Set();
    this.debug = false;
  }

  log(msg) {
    if (this.debug) {
      console.log("🛒 [CartDiscount]:", msg);
    }
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.open = this.querySelector(".insert-code");
    if (!this.dataset.open) {
      this.dataset.open = "closed";
    }

    if (this.form) {
      this.form.setAttribute(
        "aria-hidden",
        this.dataset.open === "opened"
          ? "false"
          : "true"
      );
    }
    this.error = this.querySelector(".discount-error");
    this.discountsList = this.querySelector(".applied-discounts-list");

    if (this.form) {
      this.form.addEventListener("submit", this.apply);
    }

    if (this.open) {
      this.open.addEventListener("click", this.toggle);
    }

    this.removeBtn = this.querySelector(".remove-discount");
    if (this.removeBtn) {
      this.removeBtn.addEventListener("click", this.clearAllDiscounts);
    }

    // Garantir que o campo input sempre venha limpo
    this.clearDiscountInput();

    // Carregar descontos salvos do localStorage
    this.loadSavedDiscounts();
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener("submit", this.apply);
    if (this.open) this.open.removeEventListener("click", this.toggle);
    this.teardownRefazer();
  }

  async loadSavedDiscounts() {
    const saved = localStorage.getItem("appliedDiscounts");

    if (saved) {
      try {
        const discounts = JSON.parse(saved);
        this.appliedDiscounts = new Set(discounts);
      } catch (e) {
        console.error("Erro ao carregar descontos salvos:", e);
      }
    }

    await this.syncWithCartDiscounts();
    this.renderDiscountsList();
  }

  syncFromCartData(cartData) {
    const currentDiscounts = new Set();

    if (
      cartData?.discount_codes &&
      Array.isArray(cartData.discount_codes)
    ) {
      cartData.discount_codes.forEach((discount) => {
        if (discount.applicable) {
          currentDiscounts.add(discount.code);
        }
      });
    }

    this.appliedDiscounts = currentDiscounts;

    this.saveDiscounts();
    this.renderDiscountsList();

    this.log("Descontos sincronizados com carrinho:", [
      ...currentDiscounts,
    ]);
  }

  async syncWithCartDiscounts() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        return null;
      }

      const cartData = await response.json();

      this.syncFromCartData(cartData);

      return cartData;
    } catch (error) {
      console.error(
        "Erro ao sincronizar descontos com carrinho:",
        error
      );

      return null;
    }
  }

  saveDiscounts() {
    localStorage.setItem(
      "appliedDiscounts",
      JSON.stringify([...this.appliedDiscounts])
    );
  }

  clearDiscountInput() {
    const discountInput = this.form?.querySelector('[name="discount"]');
    if (discountInput) {
      discountInput.value = "";
    }
  }

  toggle = (e) => {
    e.preventDefault();

    const isOpen = this.dataset.open === "opened";

    this.dataset.open = isOpen ? "closed" : "opened";

    if (this.form) {
      this.form.setAttribute(
        "aria-hidden",
        isOpen ? "true" : "false"
      );
    }

    this.dispatchEvent(
      new CustomEvent(ThemeEvents.DISCOUNT_TOGGLE, {
        detail: {
          open: !isOpen,
          sectionId: this.dataset.sectionId
        },
      })
    );

    this.clearDiscountInput();
    this.error.hidden = true;

    this.open.textContent =
      isOpen ? "Inserir código" : "Fechar";
  };

  apply = async (e) => {
    e.preventDefault();
    const discountCode = this.form
      .querySelector('[name="discount"]')
      .value.trim();

    if (!discountCode) {
      this.error.textContent = "Por favor, insira um código de desconto.";
      this.error.hidden = false;
      return;
    }

    if (this.appliedDiscounts.has(discountCode)) {
      this.error.textContent = "Este código de desconto já foi aplicado.";
      this.error.hidden = false;
      return;
    }

    // Adicionar estado de loading
    const submitButton =
      this.form.querySelector('button[type="submit"]') ||
      this.form.querySelector('input[type="submit"]');

    const loadingSpinner = submitButton?.querySelector(".form-loading");
    const defaultIcon = submitButton?.querySelector(":scope > svg");

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add("is-loading");

      defaultIcon?.classList.add("hidden");
      loadingSpinner?.classList.remove("hidden");
    }

    try {
      // Para múltiplos descontos, enviar todos os códigos como string separada por vírgula
      const allDiscounts = [...this.appliedDiscounts, discountCode];
      const discountString = allDiscounts.join(",");

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: discountString,
        }),
      });

      const data = await response.json();

      if (data.status && data.status !== 200) {
        this.error.textContent = "O cupom não se aplica nessa compra.";
        this.error.hidden = false;
        return;
      }

      const cartData =
        (await this.syncWithCartDiscounts()) || data;

      const appliedDiscount = Array.isArray(
        cartData?.discount_codes
      )
        ? cartData.discount_codes.find((discount) => {
            return (
              String(discount?.code || "").toLowerCase() ===
              discountCode.toLowerCase()
            );
          })
        : null;

      const wasApplied =
        appliedDiscount?.applicable === true;

      this.renderDiscountsList();

      if (!wasApplied) {
        this.error.textContent =
          "O cupom não se aplica nessa compra.";
        this.error.hidden = false;

        return;
      }

      this.error.hidden = true;
      this.clearDiscountInput();

      document.dispatchEvent(
        new DiscountUpdateEvent(
          cartData,
          this.dataset.sectionId
        )
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);
      this.error.textContent = "Erro ao aplicar desconto. Tente novamente.";
      this.error.hidden = false;
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove("is-loading");

        defaultIcon?.classList.remove("hidden");
        loadingSpinner?.classList.add("hidden");
      }
    }
  };

renderDiscountsList() {
  if (!this.discountsList) return;

  const discountsArray = [...this.appliedDiscounts];

  if (discountsArray.length === 0) {
    this.classList.remove("discount-applied");
    this.discountsList.innerHTML = "";
    this.discountsList.hidden = true;
    return;
  }

  const label = discountsArray.length === 1 ? "Cupom" : "Cupons";
  const status = discountsArray.length === 1 ? "aplicado" : "aplicados";
  const codes = discountsArray
    .map((code) => {
      const safeCode = String(code)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");

      return `<strong class="digi-cart-coupon-success__code">${safeCode}</strong>`;
    })
    .join(
      '<span class="digi-cart-coupon-success__separator" aria-hidden="true">+</span>'
    );

  this.classList.add("discount-applied");

  this.discountsList.innerHTML = `
    <div class="digi-cart-coupon-success" role="status">
      <span class="digi-cart-coupon-success__icon" aria-hidden="true">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 12.5L9.2 16.5L19 7"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>

      <span class="digi-cart-coupon-success__text">
        <span>${label}</span>
        <span class="digi-cart-coupon-success__codes">${codes}</span>
        <span>${status}</span>
      </span>

      <button
        type="button"
        class="digi-cart-coupon-success__remove"
        aria-label="Remover cupom"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 6L18 18M18 6L6 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
  `;

  this.discountsList.hidden = false;

  const removeButton = this.discountsList.querySelector(
    ".digi-cart-coupon-success__remove"
  );

  removeButton?.addEventListener("click", (event) => {
    event.preventDefault();
    this.clearAllDiscounts(removeButton);
  });
}

  removeSingleDiscount = async (discountCode, buttonElement = null) => {
    try {
      // Adicionar estado de loading ao botão
      if (buttonElement) {
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }

      // Remover da lista local
      this.appliedDiscounts.delete(discountCode);

      // Estratégia: limpar todos os cupons e reaplicar os restantes
      const remainingDiscounts = [...this.appliedDiscounts];

      // Primeiro, limpar todos os cupons
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      // Se houver cupons restantes, reaplica-los um por um com delay
      if (remainingDiscounts.length > 0) {
        for (let i = 0; i < remainingDiscounts.length; i++) {
          const code = remainingDiscounts[i];
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ discount: code }),
          });

          // Pequeno delay entre as aplicações para garantir processamento
          if (i < remainingDiscounts.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }

      // Buscar dados atualizados do carrinho
      const finalResponse = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const data = await finalResponse.json();

      this.saveDiscounts();
      await this.syncWithCartDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Erro ao remover desconto:", error);

      // Restaurar botão em caso de erro
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#7045F5"></path>
          </svg>
        `;
      }
    }
  };

  clearAllDiscounts = async (buttonElement = null) => {
    const successBanner = buttonElement?.closest(
      ".digi-cart-coupon-success"
    );

    const originalButtonContent = buttonElement?.innerHTML;

    try {
      if (buttonElement) {
        buttonElement.disabled = true;
        buttonElement.classList.add("is-loading");

        successBanner?.classList.add("is-removing");

        buttonElement.innerHTML = `
          <span class="digi-cart-coupon-success__removing">
            Removendo...
          </span>

          <svg
            class="digi-cart-coupon-success__removing-spinner"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="2.5"
              fill="none"
              stroke-dasharray="28"
              stroke-dashoffset="28"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="28;0"
                dur="0.8s"
                repeatCount="indefinite"
              />
            </circle>
          </svg>
        `;
      }

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      const data = await response.json();

      this.appliedDiscounts.clear();
      this.saveDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
      } catch (error) {
        console.error(error);

        if (buttonElement) {
          buttonElement.disabled = false;
          buttonElement.classList.remove("is-loading");

          successBanner?.classList.remove("is-removing");

          if (originalButtonContent) {
            buttonElement.innerHTML = originalButtonContent;
          }
        }
      }
  };

  teardownRefazer() {
    // Método de limpeza para a classe CartDiscount
    // Por enquanto vazio, pode ser expandido conforme necessário
  }
}

customElements.define("cart-discount-component", CartDiscount);

window.addEventListener("pageshow", async (event) => {
  const navigationEntry =
    performance.getEntriesByType("navigation")[0];

  const isHistoryNavigation =
    event.persisted ||
    navigationEntry?.type === "back_forward";

  if (!isHistoryNavigation) {
    return;
  }

  try {
    const response = await fetch("/cart.js", {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    });

    if (!response.ok) {
      return;
    }

    const cartData = await response.json();

    document
      .querySelectorAll("cart-discount-component")
      .forEach((component) => {
        if (
          typeof component.syncFromCartData === "function"
        ) {
          component.syncFromCartData(cartData);
        }
      });

    document.dispatchEvent(
      new DiscountUpdateEvent(
        { cart: cartData },
        "checkout-history"
      )
    );
  } catch (error) {
    console.error(
      "Erro ao atualizar carrinho após retornar do checkout:",
      error
    );
  }
});

class CartShipping extends HTMLElement {
  log(...args) {
    if (window.DIGI_CART_DEBUG === true) {
      console.debug('[CartShipping]', ...args);
    }
  }

  fmtBRL(v) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(v) || 0);
  }

  deliveryHtml(range) {
    const _isDateLike = (v) =>
      typeof v === "string" && !Number.isNaN(Date.parse(v));
    const _toDate = (v) => {
      if (v == null) return null;
      if (typeof v === "number" && Number.isFinite(v)) {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + v);
        return d;
      }
      if (_isDateLike(v)) return new Date(v);
      return null;
    };
    const _fmt = (d) =>
      d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    const _rel = (d) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cmp = new Date(d);
      cmp.setHours(0, 0, 0, 0);
      const diff = Math.round((cmp - today) / 86400000);
      if (diff === 0) return "Hoje";
      if (diff === 1) return "Amanhã";
      if (diff > 1 && diff <= 7) {
        const wd = cmp.toLocaleDateString("pt-BR", { weekday: "short" });
        return `${wd} (${_fmt(cmp)})`;
      }
      return _fmt(cmp);
    };

    if (!range) return "";
    if (typeof range === "string") return range;

    let min = range.min ?? range.minimum ?? range.from ?? range[0] ?? null;
    let max = range.max ?? range.maximum ?? range.to ?? range[1] ?? null;
    const unit = String(range.unit || range.units || range.type || "");

    if (typeof min === "number" || typeof max === "number") {
      const same = (min ?? max) === (max ?? min);
      const qtd = same ? min ?? max : `${min ?? "?"}–${max ?? "?"}`;
      const uteis = unit.includes("business");
      return `em ${qtd} ${uteis ? "dias úteis" : "dias"}`;
    }

    const dMin = _toDate(min),
      dMax = _toDate(max);
    if (dMin && dMax) {
      if (dMin.toDateString() === dMax.toDateString()) return _rel(dMin);
      const diff = Math.max(0, Math.round((dMax - dMin) / 86400000));
      const a = _rel(dMin),
        b = diff <= 14 ? _rel(dMax) : _fmt(dMax);
      return `entre ${a} e ${b}`;
    }
    if (dMin) return `a partir de ${_rel(dMin)}`;
    if (dMax) return `até ${_rel(dMax)}`;
    return "";
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.open = this.querySelector(".insert-cep");
    this.error = this.querySelector(".shipping-component-error");

    if (this.form) this.form.addEventListener("submit", this.apply);
    if (this.open) this.open.addEventListener("click", this.toggle);

    const savedRates = localStorage.getItem("shippingRates");
    const selectedRate = localStorage.getItem("selectedShippingRate");

    if (savedRates && selectedRate) {
      this.renderSelected(JSON.parse(selectedRate));
    } else if (savedRates) {
      // Se há rates salvos mas não há seleção, selecionar automaticamente o mais barato
      const rates = JSON.parse(savedRates);
      rates.sort((a, b) => a.price - b.price);
      const cheapestRate = rates[0];
      localStorage.setItem(
        "selectedShippingRate",
        JSON.stringify(cheapestRate)
      );
      this.renderSelected(cheapestRate);
    }
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener("submit", this.apply);
    this.teardownRefazer();
  }

  toggle = (e) => {
    e.preventDefault();
    const isOpen = this.dataset.open === "opened";
    if (isOpen) {
      this.removeAttribute("data-open");
    } else {
      this.dataset.open = "opened";
    }

    if (isOpen) {
      const cepInput = this.form?.querySelector('[name="shipping-component"]');
      if (cepInput) cepInput.value = "";
      this.error.hidden = true;
    }
    this.open.textContent = isOpen ? "Calcule seu frete" : "Fechar";
  };

  apply = async (e) => {
    e.preventDefault();

    const raw =
      this.form.querySelector('[name="shipping-component"]')?.value || "";
    const zip = raw.replace(/\D/g, "");
    if (zip.length !== 8) {
      this.error.textContent = "Informe um CEP válido (8 dígitos).";
      this.error.hidden = false;
      return;
    }

    const spinner = this.form.querySelector(".form-loading");
    spinner.classList.remove("hidden");
    this.error.hidden = true;

    try {
      // Salvar o CEP para recálculos automáticos futuros
      localStorage.setItem("lastCalculatedCep", zip);

      // Executar cálculo usando o método refatorado
      await this.calculateShippingRates(zip);

      // Fechar o formulário após calcular
      this.dataset.open = "closed";
      this.open.textContent = "Calcule seu frete";

      // Recarregar página se estiver na URL /cart após selecionar frete
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (err) {
      console.error(err);
      this.error.textContent =
        err.message || "Erro ao consultar frete. Tente novamente.";
      this.error.hidden = false;
    } finally {
      spinner.classList.add("hidden");
    }
  };

  // helper de polling (coloque dentro da mesma classe)
  pollRates = async (url, { tries = 12, delay = 500 } = {}) => {
    for (let i = 0; i < tries; i++) {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length)
          return data.shipping_rates;
      }
      await new Promise((r) => setTimeout(r, delay));
    }
    return [];
  };

  renderList(rates) {
    // Método mantido para compatibilidade, mas agora seleciona automaticamente a opção mais barata
    rates.sort((a, b) => a.price - b.price);
    const cheapestRate = rates[0];
    localStorage.setItem("selectedShippingRate", JSON.stringify(cheapestRate));
    this.renderSelected(cheapestRate);

    // Recarregar página se estiver na URL /cart após selecionar frete
    if (window.location.pathname === "/cart") {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }

  renderSelected(selected) {
    const listBox = this.querySelector(".selected-shipping-components");
    const wrapper = listBox.querySelector(".shipping-wrapper");
    let resume = listBox.querySelector(".shipping-resume");
    if (!resume) {
      resume = document.createElement("div");
      resume.className = "shipping-resume";
      listBox.appendChild(resume);
    }

    wrapper.style.display = "none";
    listBox.classList.remove("hidden");
    this.classList.add("shipping-component-applied");

    const prazo = this.deliveryHtml(selected.delivery_range);
    resume.innerHTML = `
      <li class="shipping-component-item">
        <span class="shipping-selected-wrapper">
          <span>${selected.name} ${prazo ? `• até ${prazo}` : ""}</span>
        </span>

        <span class="shipping-component-actions">
          <span class="shipping-component-price">
            ${
              Number(selected.price) === 0
                ? "Grátis"
                : this.fmtBRL(selected.price)
            }
          </span>

          <button
            type="button"
            class="remove-shipping-component"
            aria-label="Refazer cálculo"
          >
            &#10005;
          </button>
        </span>
      </li>
    `;

    this.setAttribute("data-open", "closed");
    this.teardownRefazer?.();
    this._refazerBtn = resume.querySelector(".remove-shipping-component");
    this._refazerBtn?.addEventListener("click", this.clearShipping);
  }

  teardownRefazer() {
    if (this._refazerBtn) {
      this._refazerBtn.removeEventListener("click", this.clearShipping);
      this._refazerBtn = null;
    }
  }

  clearShipping = () => {
    localStorage.removeItem("shippingRates");
    localStorage.removeItem("selectedShippingRate");
    localStorage.removeItem("lastCalculatedCep"); // Limpar CEP salvo também

    const listBox = this.querySelector(".selected-shipping-components");
    const wrapper = listBox?.querySelector(".shipping-wrapper");
    const resume = listBox?.querySelector(".shipping-resume");

    if (resume) resume.remove(); // remove apenas o resumo
    if (wrapper) wrapper.style.display = ""; // volta a lista pro display padrão
    listBox?.classList.add("hidden");

    this.classList.remove("shipping-component-applied");
    this.removeAttribute("data-open");
    this.open.textContent = "Calcule seu frete";

    const cepInput = this.form?.querySelector('[name="shipping-component"]');

    if (cepInput) {
      cepInput.value = "";
    }

    // Recarregar página se estiver na URL /cart
    if (window.location.pathname === "/cart") {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  // Método para recalcular automaticamente o frete quando o carrinho muda
  autoRecalculateShipping = async () => {
    this.log("🚀 Iniciando recálculo automático do frete...");

    // Verificar se há um CEP salvo anteriormente
    const savedCep = localStorage.getItem("lastCalculatedCep");
    if (!savedCep || savedCep.length !== 8) {
      this.log("❌ CEP não encontrado ou inválido:", savedCep);
      return; // Não há CEP válido salvo
    }

    // Só recalcular se não estivermos na página do carrinho (para evitar reload infinito)
    if (window.location.pathname === "/cart") {
      this.log("❌ Evitando recálculo na página /cart");
      return;
    }

    this.log("✅ CEP válido encontrado:", savedCep);

    try {
      // Mostrar skeleton loading
      this.log("🎨 Mostrando skeleton loading...");
      this.showRecalculatingState();

      // Adicionar delay para garantir que a animação seja visível
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Simular o preenchimento do formulário e cálculo
      const cepInput = this.form?.querySelector('[name="shipping-component"]');
      if (cepInput) {
        cepInput.value = savedCep;
      }

      // Executar o cálculo automaticamente
      this.log("🔄 Calculando frete...");
      await this.calculateShippingRates(savedCep);

      // Adicionar delay antes de esconder para ver o resultado
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Esconder skeleton loading
      this.log("✅ Escondendo skeleton loading...");
      this.hideRecalculatingState();
    } catch (error) {
      this.log("❌ Erro ao recalcular frete automaticamente:", error);
      // Esconder skeleton loading em caso de erro
      this.hideRecalculatingState();
    }
  };

  // Métodos para controlar a animação de recálculo
  showRecalculatingState = () => {
    this.log("🎨 showRecalculatingState chamado");
    const listBox = this.querySelector(".selected-shipping-components");
    const resume = listBox?.querySelector(".shipping-resume");

    this.log("📦 listBox:", listBox);
    this.log("📄 resume:", resume);

    if (resume) {
      this.log("✅ Adicionando classe shipping-recalculating");
      this.classList.add("shipping-recalculating");
    } else {
      this.log("🔧 Criando skeleton temporário");
      // Se não há frete selecionado, criar skeleton temporário
      this.createTemporarySkeleton();
    }
  };

  hideRecalculatingState = () => {
    this.log("🎨 hideRecalculatingState chamado");
    this.classList.remove("shipping-recalculating");
    this.removeTemporarySkeleton();
  };

  createTemporarySkeleton = () => {
    this.log("🔧 createTemporarySkeleton chamado");
    const listBox = this.querySelector(".selected-shipping-components");
    if (!listBox || listBox.querySelector(".shipping-skeleton-temp")) {
      this.log("❌ listBox não encontrado ou skeleton já existe");
      return;
    }

    const skeletonHtml = `
      <div class="shipping-skeleton-temp" style="padding: 10px 0;">
        <div class="shipping-skeleton wide"></div>
        <div class="shipping-skeleton medium"></div>
        <div class="shipping-skeleton narrow"></div>
      </div>
    `;

    this.log("✅ Inserindo skeleton HTML");
    listBox.insertAdjacentHTML("beforeend", skeletonHtml);
    listBox.classList.remove("hidden");
  };

  removeTemporarySkeleton = () => {
    this.log("🗑️ removeTemporarySkeleton chamado");
    const skeleton = this.querySelector(".shipping-skeleton-temp");
    if (skeleton) {
      this.log("✅ Removendo skeleton");
      skeleton.remove();
    } else {
      this.log("❌ Skeleton não encontrado para remover");
    }
  };

  calculateShippingRates = async (zip) => {
    try {
      // 1) PREPARE
      const prepare = await fetch("/cart/prepare_shipping_rates.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          shipping_address: {
            zip,
            country: "Brazil",
            province: "MG",
          },
        }),
      });

      if (!prepare.ok) {
        const err = await prepare.json().catch(() => ({}));
        throw new Error(
          err?.errors || "Não foi possível iniciar o cálculo de frete."
        );
      }

      // 2) POLLING (GET) até ter tarifas
      const params = new URLSearchParams({
        "shipping_address[zip]": zip,
        "shipping_address[country]": "Brazil",
        "shipping_address[province]": "MG",
      });
      const rates = await this.pollRates(`/cart/shipping_rates.json?${params}`);

      if (!rates.length) {
        // Se não há rates disponíveis, limpar seleção anterior
        localStorage.removeItem("shippingRates");
        localStorage.removeItem("selectedShippingRate");
        return;
      }

      rates.sort((a, b) => a.price - b.price);
      localStorage.setItem("shippingRates", JSON.stringify(rates));

      // Selecionar automaticamente a opção mais barata (primeira após ordenação)
      const cheapestRate = rates[0];
      localStorage.setItem(
        "selectedShippingRate",
        JSON.stringify(cheapestRate)
      );
      this.renderSelected(cheapestRate);
    } catch (error) {
      this.log("Erro ao calcular frete:", error);
    }
  };
}

customElements.define("cart-shipping-component", CartShipping);
